import { useCallback, useState } from 'react';
import classes from 'classnames';
import * as RadixSlider from '@radix-ui/react-slider';
import { twMerge } from 'tailwind-merge';
import HandleIcon from 'assets/icons/vertical-hamburger.svg?react';
import styles from './Slider.module.scss';
import { format } from 'utils/format';

export type Props = {
  min: number;
  max: number;
  value?: number;
  heading?: string;
  headingStyle?: string;
  step?: number;
  units?: string;
  onChange?: (value: number) => void;
  onAfterChange: (value: number) => void;
  dataTest?: string;
  hideAmountHeader?: boolean;
  unitsTransform?: (value: number) => string;
  size?: 'default' | 'compact';
  disabled?: boolean;
};

export default function Slider({
  min,
  max,
  value,
  heading,
  headingStyle,
  step,
  units,
  onChange,
  onAfterChange,
  hideAmountHeader,
  dataTest = 'slider',
  unitsTransform,
  disabled = false,
}: Props) {
  const leftMargin = min + (max - min) * 0.1;
  const rightMargin = max - (max - min) * 0.1;

  // Parents update `value` only in onAfterChange, so keep the value while
  // dragging locally, otherwise the thumb would not move
  const [dragValue, setDragValue] = useState<number>();
  const thumbValue = dragValue ?? value ?? min;
  const unitsStr = unitsTransform ? unitsTransform(thumbValue) : units;

  const handleValueChange = useCallback(
    ([newValue]: number[]) => {
      setDragValue(newValue);
      onChange?.(newValue);
    },
    [onChange],
  );

  // Radix skips onValueCommit when a drag ends on its starting value
  const clearDragValue = useCallback(() => setDragValue(undefined), []);

  const handleValueCommit = useCallback(
    ([newValue]: number[]) => {
      setDragValue(undefined);
      onAfterChange(newValue);
    },
    [onAfterChange],
  );

  return (
    <div data-test={dataTest} className="group">
      {heading && !hideAmountHeader && (
        <div
          className={twMerge(
            'text-lg font-semibold pb-12 sn: pt-3',
            headingStyle,
          )}
          data-test={`${dataTest}-header`}
        >
          {heading}
        </div>
      )}
      <RadixSlider.Root
        className="relative flex items-center h-10 touch-none select-none"
        min={min}
        max={max}
        step={step}
        value={[thumbValue]}
        onValueChange={handleValueChange}
        onValueCommit={handleValueCommit}
        onPointerUp={clearDragValue}
        disabled={disabled}
      >
        <RadixSlider.Track className={classes('relative grow', styles.track)}>
          <RadixSlider.Range className={classes('absolute', styles.range)} />
        </RadixSlider.Track>
        {/* Thumb */}
        <RadixSlider.Thumb
          className="relative top-1 flex justify-center items-center bg-background rounded-full cursor-grab shadow-[0px_-1px_1px_0px_rgba(0,0,0,0.11)_inset,0px_1px_4px_0px_rgba(0,0,0,0.44)] h-9 w-9 outline-none focus-visible:ring-2 focus-visible:ring-primary"
          aria-label={heading}
          data-test={`${dataTest}-handler`}
        >
          <HandleIcon
            className="h-4 w-4 fill-primary"
            data-test={`${dataTest}-handle-icon`}
          />

          {/* Tooltip on top of the handle */}
          <div
            className={classes(
              twMerge(
                'transition-opacity absolute rounded-lg bg-background shadow-lg after:absolute after:h-3 after:w-3  after:bg-background after:-bottom-[5px] after:left-[calc(50%-6px)] after:rotate-45 bottom-12',
                thumbValue > rightMargin &&
                  'right-0 after:left-[calc(100%-22px)]',
                thumbValue < leftMargin && 'left-0 after:left-3',
              ),
            )}
          >
            <div className="flex justify-center items-baseline min-w-14 px-2 pb-2 gap-1">
              <span data-test="thumb" className="text-2xl font-bold">
                {format.number(thumbValue, 'amount')}
              </span>
              {units && (
                <span className="text-sm font-semibold" data-test="unit">
                  {unitsStr}
                </span>
              )}
            </div>
          </div>
        </RadixSlider.Thumb>
      </RadixSlider.Root>
      <div className="mt-1 text-muted text-xs flex justify-between">
        <span data-test={`${dataTest}-min`}>
          {min} {unitsTransform ? unitsTransform(min) : units}
        </span>
        <span data-test={`${dataTest}-max`}>
          {max} {unitsTransform ? unitsTransform(max) : units}
        </span>
      </div>
    </div>
  );
}
